import React, { useCallback } from 'react';
import { ApplicationView, useController } from '@aha-app/mvc';
import ToolbarHDivider from './ToolbarHDivider';
import tools from '../tools';
import styles from '../styles.module.css';
import classNames from 'classnames';
import Tippy, { useSingleton } from '@tippyjs/react';
import { TOOLTIP_DELAY_GENERAL } from '../constants';
import useResizeObserver from '../components/hooks/useResizeObserver';
import WhiteboardController from '../controllers/WhiteboardController';
import { type Rect } from '../types';
import { type ToolList } from '../fabric/ObjectBehaviors';
import { TippyTargetProvider } from '../components/tippyContext';
import { tippyPopoverPlugin } from '../utils/tippyPopoverPlugin';

function filterTools(
  visibleTools: ToolList,
  controller: WhiteboardController,
  selection: fabric.Object
): ToolList {
  return visibleTools.filter(tool => {
    if (Array.isArray(tool)) {
      const visible = filterTools(tool, controller, selection);
      return visible.length > 0;
    } else {
      return tool.available({ controller, selection });
    }
  });
}

const toolComponents = ({
  selection,
  visibleTools,
  controller,
  forceRecalculate,
}: {
  selection: fabric.Object;
  visibleTools: ToolList;
  controller: WhiteboardController;
  forceRecalculate?: () => void;
}) => {
  return visibleTools.map((tool, i) => {
    if (Array.isArray(tool)) {
      tool = filterTools(tool, controller, selection);
      if (tool.length > 0) {
        return (
          <React.Fragment key={`fragment-${i}`}>
            {i > 0 && <ToolbarHDivider key={`divider-${i}`} />}
            {toolComponents({
              selection,
              visibleTools: tool,
              controller,
            })}
          </React.Fragment>
        );
      } else {
        return null;
      }
    } else {
      const ButtonComponent = tool.buttonComponent()!;
      return (
        <ButtonComponent
          key={tool.toolName}
          selection={selection}
          controller={controller}
          forceRecalculate={forceRecalculate}
          tool={tool}
        />
      );
    }
  });
};

const LEFT_MARGIN = 85;
const RIGHT_MARGIN = 20;
const VERTICAL_OFFSET = 40;
const BOTTOM_MARGIN = 120;
// How far the left side of the BaselineToolbarContainer is from the right hand
// side of the scren.
const BASELINE_TOOLBAR_SPACE = 400;
const TOP_MARGIN = 20;

// Algorithm to position toolbar relative to the selection rect.
// We prefer the top, but will move to the bottom if necessary, and slide
// left or right to prevent it going off the screen. If the object is off the
// screen completely then don't show the toolbar at all.
//
// This has been implemented mostly with constants to keep the complexity down.
// The ultimate solution would take into account the exact position and sizes
// of all the toolbars and ensure they don't collide.
export function setToolbarPositionStyles(
  selectionRect: Rect,
  toolbarElement: HTMLElement,
  canvasViewport: Rect
) {
  const dimensions = toolbarElement.getBoundingClientRect();

  const toolbarLeft = Math.max(
    LEFT_MARGIN,
    Math.min(
      selectionRect.left + selectionRect.width / 2 - dimensions.width / 2,
      canvasViewport.width - dimensions.width - RIGHT_MARGIN
    )
  );

  let toolbarTop: number;
  // If the selected object overlaps where we want to put the toolbar at the
  // top, then move it to the bottom. But if there isn't enough space there
  // either, move it to the top, overlapping the object.
  if (selectionRect.top > dimensions.height + VERTICAL_OFFSET) {
    toolbarTop = selectionRect.top - (dimensions.height + VERTICAL_OFFSET);
  } else {
    toolbarTop = selectionRect.top + selectionRect.height + VERTICAL_OFFSET;

    const maxTopPosition =
      canvasViewport.height - dimensions.height - RIGHT_MARGIN;

    if (toolbarTop > maxTopPosition) {
      toolbarTop = TOP_MARGIN;
    }
  }

  // Prevent collision with the BaselineToolbarContainer
  if (toolbarTop > canvasViewport.height - BOTTOM_MARGIN) {
    const toolbarRight = toolbarLeft + dimensions.width;

    if (toolbarRight > canvasViewport.width - BASELINE_TOOLBAR_SPACE) {
      toolbarTop = canvasViewport.height - BOTTOM_MARGIN;
    }
  }

  toolbarElement.style.left = `${toolbarLeft}px`;
  toolbarElement.style.top = `${toolbarTop}px`;
}

// The selection rect is a prop so that the toolbar rerenders when the selected
// object moves on the canvas.
const ObjectToolbar: React.FC<{
  rect: Rect;
}> = ({ rect }) => {
  const controller = useController(WhiteboardController);
  const { toolbarReRenderCount } = controller.state;
  const [tippySingletonSource, tippySingletonTarget] = useSingleton();

  const visibleTools = controller.visibleObjectTools();
  const selection = controller.toolbarObject();
  const calculateDimensions = useCallback(() => {
    const viewportRect = controller.canvas.vptCoordsAsWindowCoords();

    if (!viewportRect || !toolbarRef.current) {
      return;
    }

    return setToolbarPositionStyles(rect, toolbarRef.current, viewportRect);
  }, [controller.canvas, rect.height, rect.left, rect.top, rect.width]);

  const toolbarRef = useResizeObserver<HTMLDivElement>(calculateDimensions);

  if (visibleTools === null) return <Tippy singleton={tippySingletonSource} />;
  const MenuComponent = tools.ToolMenu.buttonComponent();
  const menuAvailable = tools.ToolMenu.available({ controller, selection });

  return (
    <div
      className={classNames(styles.objectToolbar, styles.toolbarCommon)}
      ref={toolbarRef}
      key={selection.id}
      data-testid='Object-toolbar'
    >
      <Tippy
        singleton={tippySingletonSource}
        delay={TOOLTIP_DELAY_GENERAL}
        zIndex={12000}
        plugins={[tippyPopoverPlugin]}
        onAfterUpdate={instance => {
          // When we update the toolbar position, we may shift the toolbar just
          // enough that the toolbar is still hovered, but a tool is not, resulting
          // in an empty tooltip that should be hidden
          const content = instance.props.content;
          if (typeof content === 'object' && content.childNodes.length === 0) {
            instance.hide();
          }
        }}
      />
      <TippyTargetProvider
        value={tippySingletonTarget}
        key={toolbarReRenderCount}
      >
        {toolComponents({
          selection,
          visibleTools: filterTools(visibleTools, controller, selection),
          controller,
          forceRecalculate: calculateDimensions,
        })}
        {visibleTools.length > 0 && menuAvailable && (
          <ToolbarHDivider key='divider-menu' />
        )}
        {menuAvailable && (
          <MenuComponent
            key='menu'
            selection={selection}
            tool={tools.ToolMenu}
            controller={controller}
          />
        )}
      </TippyTargetProvider>
    </div>
  );
};

export default ApplicationView(ObjectToolbar);
